import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [CommonModule, ButtonModule, RouterModule],
  template: `
    <div class="landing-page">
      <section class="hero">
        <div class="hero-content">
          <div class="badge">Clean, Simple Cash Accounting</div>
          <h1>Modern Accounting for Modern Businesses</h1>
          <p class="subtitle">CashZap gives you bulletproof double-entry accounting with the power of AI. Built for precision, simplified for you.</p>
          <div class="hero-actions">
            <button pButton label="Go to Demo" icon="pi pi-play" class="p-button-lg shadow-4" (click)="goToDemo()"></button>
            <button pButton label="Features" icon="pi pi-sparkles" class="p-button-lg p-button-outlined" (click)="scrollToFeatures()"></button>
          </div>
        </div>
        <div class="hero-visual">
           <div class="chart-mockup">
             <img src="/growth_chart.png" alt="Financial Growth Chart" class="floating-image">
           </div>
        </div>
      </section>

      <section id="features" class="features">
        <div class="section-title">
          <span class="uptitle">Core Capabilities</span>
          <h2>Precision in Every Entry</h2>
          <p>Everything you need to manage your business finances with absolute confidence.</p>
        </div>
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-image">
              <img src="/Ledger.jpg" alt="Double-Entry Ledger">
            </div>
            <div class="feature-content">
              <div class="icon-box"><i class="pi pi-shield"></i></div>
              <h3>Double-Entry Ledger</h3>
              <p>Built on time-tested accounting principles. Every debit has a credit, ensuring your books are always accurate and ready for audit.</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-image">
              <img src="/reports.jpg" alt="Power Reports">
            </div>
            <div class="feature-content">
              <div class="icon-box"><i class="pi pi-chart-bar"></i></div>
              <h3>Power Reports</h3>
              <p>Instant access to Profit & Loss statements, Balance Sheets, and Cash Flow summaries. Visual data for better decision making.</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-image">
              <img src="/chart_of_accounts.jpg" alt="Chart of Accounts">
            </div>
            <div class="feature-content">
              <div class="icon-box"><i class="pi pi-list"></i></div>
              <h3>Chart of Accounts</h3>
              <p>Organize your finances with a flexible, professional Chart of Accounts. Full support for assets, liabilities, equity, revenue, and expenses.</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-image">
              <img src="/audit.jpg" alt="Audit Trail">
            </div>
            <div class="feature-content">
              <div class="icon-box"><i class="pi pi-history"></i></div>
              <h3>Audit Trail</h3>
              <p>Experience complete transparency. Detailed audit logs track every change, ensuring accountability and security for your financial records.</p>
            </div>
          </div>
        </div>
      </section>

      <section class="demo-cta">
        <div class="cta-card shadow-8">
          <h2>Experience the future of accounting</h2>
          <p>Join thousands of businesses simplifying their finances with CashZap. No complex setup involved.</p>
          <button pButton label="Launch Demo Mode" icon="pi pi-play" class="p-button-lg p-button-success p-button-raised" (click)="goToDemo()"></button>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .landing-page {
      background: var(--app-surface);
      color: var(--app-text);
      overflow-x: hidden;
      font-family: var(--font-family);
      margin: -1.5rem; /* Counteract parent padding */
    }
    
    @media (max-width: 768px) {
      .landing-page {
        margin: -1rem; /* Counteract mobile parent padding */
      }
    }
    
    .badge {
      display: inline-block;
      padding: 0.5rem 1rem;
      background: color-mix(in srgb, var(--primary-color), transparent 85%);
      color: var(--primary-color);
      border-radius: 999px;
      font-size: 0.875rem;
      font-weight: 600;
      margin-bottom: 2rem;
      border: 1px solid color-mix(in srgb, var(--primary-color), transparent 70%);
    }

    .hero {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8rem 8% 10rem;
      min-height: 85vh;
      gap: 6rem;
      position: relative;
    }

    .hero-content {
      max-width: 650px;
      z-index: 2;
    }

    h1 {
      font-size: 4.5rem;
      font-weight: 800;
      line-height: 1.05;
      margin-bottom: 2rem;
      letter-spacing: -0.02em;
      background: linear-gradient(135deg, var(--app-text) 0%, color-mix(in srgb, var(--app-text), var(--primary-color) 40%) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .subtitle {
      font-size: 1.4rem;
      line-height: 1.6;
      color: var(--app-text-muted);
      margin-bottom: 3.5rem;
      max-width: 540px;
    }

    .hero-actions {
      display: flex;
      gap: 1.5rem;
    }

    .hero-visual {
      flex: 1;
      display: flex;
      justify-content: center;
      position: relative;
      perspective: 1000px;
    }

    .chart-mockup {
      width: 520px;
      perspective: 1000px;
      transition: all 0.5s ease;
    }
    
    .floating-image {
      width: 100%;
      height: auto;
      border-radius: 24px;
      box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.4);
      transform: rotateY(-15deg) rotateX(5deg);
      transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }
    
    .chart-mockup:hover .floating-image {
      transform: rotateY(0deg) rotateX(0deg) translateY(-10px) scale(1.05);
      box-shadow: 0 60px 100px -30px rgba(0, 0, 0, 0.5);
    }

    .features {
      padding: 10rem 8%;
      background: color-mix(in srgb, var(--app-surface-muted), transparent 40%);
    }

    .section-title {
      text-align: center;
      margin-bottom: 8rem;
    }
    .uptitle {
      display: block;
      color: var(--primary-color);
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.2em;
      font-size: 0.875rem;
      margin-bottom: 1rem;
    }
    .section-title h2 {
      font-size: 3.25rem;
      font-weight: 800;
      margin-bottom: 1.5rem;
      letter-spacing: -0.01em;
    }
    .section-title p {
      font-size: 1.25rem;
      color: var(--app-text-muted);
      max-width: 600px;
      margin: 0 auto;
    }

    .features-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 3.5rem;
    }
    .feature-item {
      background: var(--app-surface);
      border: 1px solid var(--app-border);
      border-radius: 2rem;
      transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
    .feature-item:hover {
      transform: translateY(-12px);
      box-shadow: 0 40px 60px -20px rgba(0, 0, 0, 0.2);
      border-color: var(--primary-color);
    }
    .feature-image {
      width: 100%;
      height: 200px;
      overflow: hidden;
      border-bottom: 1px solid var(--app-border);
    }
    .feature-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
    }
    .feature-item:hover .feature-image img {
      transform: scale(1.05);
    }
    .feature-content {
      padding: 2.5rem;
    }
    .icon-box {
      width: 60px;
      height: 60px;
      background: color-mix(in srgb, var(--primary-color), transparent 92%);
      border-radius: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 2rem;
    }
    .icon-box i {
      font-size: 1.75rem;
      color: var(--primary-color);
    }
    .feature-item h3 {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 1rem;
    }
    .feature-item p {
      line-height: 1.6;
      color: var(--app-text-muted);
      font-size: 1rem;
    }

    .demo-cta {
      padding: 10rem 8%;
      text-align: center;
    }
    .cta-card {
      padding: 8rem 4rem;
      background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
      color: white;
      border-radius: 4rem;
      position: relative;
      overflow: hidden;
    }
    .cta-card h2 {
      font-size: 3.5rem;
      font-weight: 800;
      margin-bottom: 1.5rem;
      letter-spacing: -0.01em;
    }
    .cta-card p {
      font-size: 1.4rem;
      opacity: 0.7;
      margin-bottom: 4rem;
      max-width: 700px;
      margin-left: auto;
      margin-right: auto;
      line-height: 1.6;
    }

    @media (max-width: 1200px) {
      .hero {
        flex-direction: column;
        text-align: center;
        padding-top: 6rem;
        gap: 4rem;
      }
      h1 { font-size: 3.5rem; }
      .hero-content { max-width: 100%; }
      .hero-actions { justify-content: center; }
      .hero-visual { display: none; }
    }
    
    @media (max-width: 768px) {
      h1 { font-size: 2.75rem; }
      .cta-card h2 { font-size: 2.25rem; }
      .cta-card { padding: 4rem 2rem; }
      .section-title h2 { font-size: 2.25rem; }
    }
  `]
})
export class LandingComponent implements OnInit {
  constructor(private router: Router, private authService: AuthService) { }

  ngOnInit() {
  }

  goToDemo() {
    if (environment.mock) {
      if (!this.authService.isLoggedIn()) {
        // Simple auto-login for mock mode
        this.authService.signin('demo.user', 'password').subscribe({
          next: () => {
            this.router.navigate(['/dashboard']);
          },
          error: (err) => {
            console.error('Demo login failed', err);
            // Fallback to manual auth if auto-login fails
            this.router.navigate(['/auth']);
          }
        });
        return;
      }
    }
    this.router.navigate(['/dashboard']);
  }

  scrollToFeatures() {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  }
}
